import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { IResourceDatabaseFolder, IResourceFolderType, IResourceItemType } from '@/api/types';
import type { IFolderType, ISimpleResourceType } from '@/components/common';
import type { RootState } from '@/store';
import type { ISelectedFolder } from '@/store/types';

import { ButtonAdd, ResourcesBreadcrumb, ResourcesSimpleTableContainer } from '@/components/common';
import { ResourceFoldersTable } from '../ResourceFoldersTable';
import { addItemTranslationMap, getSelectedFolderName } from '@/helpers';
import { ResourceItemsContainer } from '../ResourceItemsContainer';
import { FolderModalType, ResourceModalType } from '@/hooks/enums';
import { selectFoldersList } from '@/store/selectors/resourcesSelectors';
import { useAppSelector, useFolderModal, useResourceModal, useResourceNavigation } from '@/hooks';
import { combineFoldersToShow, combineItemsToShow } from '@/store/combiners/resourcesCombiners';

const resourceFolderRouteSelector = createSelector(
  selectFoldersList,
  (_: RootState, selectedFolder: ISelectedFolder) => selectedFolder,
  (foldersList, selectedFolder) => ({
    foldersList,
    itemsToShow: combineItemsToShow(foldersList, selectedFolder),
    foldersToShow: combineFoldersToShow(foldersList, selectedFolder),
  }),
);

const ResourceFolderRoute = (): React.ReactElement => {
  const { t } = useTranslation();
  const { selectedFolder } = useResourceNavigation();
  const { foldersList, itemsToShow, foldersToShow } = useAppSelector(state =>
    resourceFolderRouteSelector(state, selectedFolder),
  );
  const { handleOpenModal: handleOpenResourceModal } = useResourceModal();
  const { handleOpenModal: handleOpenFolderModal } = useFolderModal();

  return (
    <>
      <ResourcesBreadcrumb />
      {foldersToShow.length > 0 && (
        <ResourcesSimpleTableContainer
          items={foldersToShow}
          tableTitle={t`resources.content.folders`}
          tableComponentSlot={
            <ResourceFoldersTable items={foldersToShow as Exclude<IResourceFolderType, IResourceDatabaseFolder>[]} />
          }
          tableTitleActionSlot={
            <ButtonAdd
              size="s"
              onClick={() =>
                handleOpenFolderModal({
                  modalType: FolderModalType.new,
                  data: {
                    folderType: selectedFolder.folderType as ISimpleResourceType,
                    parentFolderId: selectedFolder.folderId,
                    clientId: selectedFolder.clientId,
                  },
                })
              }
            >{t`resources.content.addFolder`}</ButtonAdd>
          }
        />
      )}
      <ResourcesSimpleTableContainer
        items={itemsToShow as IResourceItemType[]}
        tableTitle={t(getSelectedFolderName(foldersList || [], selectedFolder))}
        tableComponentSlot={
          <ResourceItemsContainer
            items={itemsToShow as IResourceItemType[]}
            parentFolderId={selectedFolder.folderId}
            clientId={selectedFolder.clientId}
          />
        }
        tableTitleActionSlot={
          <ResourceTableTitleButtonsContainer>
            {foldersToShow.length === 0 && (
              <ButtonAdd
                size="s"
                onClick={() =>
                  handleOpenFolderModal({
                    modalType: FolderModalType.new,
                    data: {
                      folderType: selectedFolder.folderType as ISimpleResourceType,
                      parentFolderId: selectedFolder.folderId,
                      clientId: selectedFolder.clientId,
                    },
                  })
                }
              >{t`resources.content.addFolder`}</ButtonAdd>
            )}
            <ButtonAdd
              size="s"
              onClick={() =>
                handleOpenResourceModal({
                  modalType: ResourceModalType.new,
                  data: {
                    resourceType: selectedFolder.folderType as ISimpleResourceType,
                    parentFolderId: selectedFolder.folderId,
                    clientId: selectedFolder.clientId,
                    existingResource: null,
                  },
                })
              }
            >
              {t(addItemTranslationMap[selectedFolder.folderType as Exclude<IFolderType, 'home'>])}
            </ButtonAdd>
          </ResourceTableTitleButtonsContainer>
        }
      />
    </>
  );
};

export default ResourceFolderRoute;

const ResourceTableTitleButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: end;
`;
